import {
  ChangeEvent,
  DragEvent,
  DragEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'

type FileDropHandlers = {
  onDragOver: DragEventHandler
  onDragEnter: DragEventHandler
  onDragLeave: DragEventHandler
  onDrop: DragEventHandler
}
type FileDropHook = {
  isDragging: boolean
  dropHandlers: FileDropHandlers
  onInputChange: (e: ChangeEvent<HTMLInputElement>) => void
}

export type DropHandler = (files: DataTransfer) => void
type UseFileDropOptions = {
  disabled?: boolean
}

function isFileDrag(e: DragEvent) {
  return Array.from(e.dataTransfer.items).some((item) => item.kind === 'file')
}

export function useFileDrop(
  onFilesPicked: DropHandler,
  { disabled = false }: UseFileDropOptions = {}
): FileDropHook {
  const [dragCounter, setDragCounter] = useState(0)

  const onDragOver = useCallback((e: DragEvent) => {
    if (disabled) {
      return
    }

    if (!isFileDrag(e)) {
      return
    }

    e.preventDefault()
  }, [disabled])

  const onDragEnter = useCallback((e: DragEvent) => {
    if (disabled) {
      return
    }

    if (!isFileDrag(e)) {
      return
    }

    setDragCounter((n) => n + 1)
  }, [disabled])

  const onDragLeave = useCallback((e: DragEvent) => {
    if (disabled) {
      return
    }

    if (!isFileDrag(e)) {
      return
    }

    setDragCounter((n) => n - 1)
  }, [disabled])

  useEffect(() => {
    function handleWindowDrop() {
      setDragCounter(0)
    }

    window.addEventListener('drop', handleWindowDrop, true)

    return () => {
      window.removeEventListener('drop', handleWindowDrop, true)
    }
  }, [])

  const onDrop = useCallback(
    (e: DragEvent) => {
      if (disabled) {
        return
      }

      if (!isFileDrag(e)) {
        return
      }

      e.preventDefault()
      e.stopPropagation()

      setDragCounter(0)

      onFilesPicked(e.dataTransfer)
    },
    [disabled, onFilesPicked]
  )

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (disabled) {
        return
      }

      const files = e.target.files
      if (!files || files.length == 0) {
        return
      }
      const dt = new DataTransfer()
      for (const f of files) dt.items.add(f)
      onFilesPicked(dt)
    },
    [disabled, onFilesPicked]
  )

  const dropHandlers = useMemo(() => {
    return { onDragOver, onDragEnter, onDragLeave, onDrop }
  }, [onDragOver, onDragEnter, onDragLeave, onDrop])

  return {
    isDragging: disabled ? false : dragCounter > 0,
    dropHandlers: dropHandlers,
    onInputChange: onInputChange
  }
}
